import {
  LuCircleAlert,
  LuTriangleAlert,
  LuHash,
  LuLayoutGrid,
} from "react-icons/lu";

export type FilterType = "all" | "fatal" | "warnings" | "ssid" | "mids";

const FILTERS: {
  key: FilterType;
  label: string;
  icon: React.ComponentType<{ size?: number; color?: string }>;
  iconColor: string;
  activeBg: string;
  activeBorder: string;
  badgeBg: string;
}[] = [
  {
    key: "all",
    label: "All",
    icon: LuLayoutGrid,
    iconColor: "#475569",
    activeBg: "#f1f5f9",
    activeBorder: "#e2e8f0",
    badgeBg: "#475569",
  },
  {
    key: "fatal",
    label: "Fatal Errors",
    icon: LuCircleAlert,
    iconColor: "#dc2626",
    activeBg: "#fef2f2",
    activeBorder: "#fecaca",
    badgeBg: "#dc2626",
  },
  {
    key: "warnings",
    label: "Warnings",
    icon: LuTriangleAlert,
    iconColor: "#b45309",
    activeBg: "#fffbeb",
    activeBorder: "#fde68a",
    badgeBg: "#92400e",
  },
  {
    key: "ssid",
    label: "SSID Errors",
    icon: LuCircleAlert,
    iconColor: "#7c3aed",
    activeBg: "#f5f3ff",
    activeBorder: "#ddd6fe",
    badgeBg: "#6d28d9",
  },
  {
    key: "mids",
    label: "MIDs",
    icon: LuHash,
    iconColor: "#003a5d",
    activeBg: "#eff6ff",
    activeBorder: "#bfdbfe",
    badgeBg: "#003a5d",
  },
];

interface ValidationFilterBarProps {
  activeFilter: FilterType;
  onFilterChange: (filter: FilterType) => void;
  counts: {
    fatal: number;
    warnings: number;
    ssid: number;
    mids: number;
  };
}

export default function ValidationFilterBar({
  activeFilter,
  onFilterChange,
  counts,
}: ValidationFilterBarProps) {
  const total = counts.fatal + counts.warnings + counts.ssid + counts.mids;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "0.75rem",
        flexWrap: "wrap",
        margin: "1.5rem 0",
      }}
    >
      {FILTERS.map(
        ({
          key,
          label,
          icon: Icon,
          iconColor,
          activeBg,
          activeBorder,
          badgeBg,
        }) => {
          const isActive = activeFilter === key;
          const count =
            key === "all" ? total : counts[key as keyof typeof counts];

          return (
            <button
              key={key}
              onClick={() => onFilterChange(key)}
              aria-pressed={isActive}
              onMouseEnter={(e) => {
                if (!isActive) {
                  e.currentTarget.style.backgroundColor =
                    "rgba(241,245,249,0.6)";
                  e.currentTarget.style.color = "#0f172a";
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive) {
                  e.currentTarget.style.backgroundColor =
                    "rgba(241,245,249,0.3)";
                  e.currentTarget.style.color = "#1e293b";
                }
              }}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "0.5rem",
                padding: "0.5rem 1rem",
                borderRadius: "0.375rem",
                fontSize: "0.875rem",
                fontWeight: 500,
                border: `1px solid ${isActive ? activeBorder : "transparent"}`,
                backgroundColor: isActive ? activeBg : "rgba(241,245,249,0.3)",
                color: isActive ? "#0f172a" : "#1e293b",
                boxShadow: isActive ? "0 1px 2px rgba(0,0,0,0.05)" : "none",
                cursor: "pointer",
                transition: "all 150ms",
                whiteSpace: "nowrap",
              }}
            >
              {key !== "all" && (
                <Icon size={15} color={iconColor} aria-hidden="true" />
              )}
              {label}
              <span
                aria-label={`${count} items`}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: "22px",
                  height: "20px",
                  padding: "0 0.375rem",
                  borderRadius: "0.375rem",
                  backgroundColor: isActive ? badgeBg : "#cbd5e1",
                  color: isActive ? "#fff" : "#334155",
                  fontSize: "11px",
                  fontWeight: 700,
                  lineHeight: 1,
                }}
              >
                {count}
              </span>
            </button>
          );
        }
      )}
    </div>
  );
}
